import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { useOrganizationResourcesClient } from "@/features/credentials/hooks/use-organization-resources-client";
import { getListItems } from "@/features/credentials/lib/get-list-items";
import { uniqueBy } from "@/features/credentials/lib/unique-by";
import type { Season } from "@/features/credentials/models/organization-resource";
import { organizationResourcesQueryKeys } from "@/features/credentials/queries/organization-resources-query-keys";

export function useSeasonsListQuery({
  sport,
  organizationId,
  competitionIds,
  enabled,
}: {
  sport: string;
  organizationId: string;
  competitionIds: string[];
  enabled: boolean;
}) {
  const { productToken } = useAuth();
  const organizationResourcesClient = useOrganizationResourcesClient();
  const sortedCompetitionIds = [...competitionIds].sort();

  return useQuery({
    queryKey: organizationResourcesQueryKeys.seasons({
      sport,
      organizationId,
      competitionIds: sortedCompetitionIds,
    }),
    queryFn: async () => {
      const responses = await Promise.all(
        sortedCompetitionIds.map((competitionId) =>
          organizationResourcesClient.getSeasons({
            productToken: productToken as string,
            sport,
            organizationId,
            competitionId,
          }),
        ),
      );

      return uniqueBy(
        responses.flatMap((response) => getListItems(response)),
        (season: Season) => String(season.seasonId ?? ""),
      );
    },
    enabled: enabled && sortedCompetitionIds.length > 0 && !!productToken && !!sport && !!organizationId,
    placeholderData: keepPreviousData,
  });
}
